import React, { useState, useEffect, useCallback } from "react";
import { FaUserPlus, FaEnvelope } from "react-icons/fa";
import Swal from "sweetalert2";
import { getReferralStats } from "../../api/referral";
import { SendEmailToChangeReferral } from "../../api/referralChange";

const InviteReferrals = () => {
  const [referralCodes, setReferralCodes] = useState([]);
  const [loadingCodes, setLoadingCodes] = useState(true);

  // Estados del formulario
  const [formData, setFormData] = useState({
    email: "",
    referralCode: "",
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Cargar códigos de referido
  useEffect(() => {
    const fetchReferralCodes = async () => {
      try {
        const codes = await getReferralStats();

        if (Array.isArray(codes)) {
          setReferralCodes(codes);
        } else {
          setReferralCodes([]);
        }
      } catch (error) {
        console.error("Error al cargar códigos de referido:", error);
        Swal.fire({
          title: "Error",
          text: "Error al cargar los códigos de referido",
          icon: "error",
          confirmButtonText: "Entendido",
          confirmButtonColor: "#ef4444",
        });
      } finally {
        setLoadingCodes(false);
      }
    };

    fetchReferralCodes();
  }, []);

  // Validación del formulario
  const validateForm = useCallback((values) => {
    const newErrors = {};

    if (!values.email) {
      newErrors.email = "El correo electrónico es requerido";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
      newErrors.email = "Ingresa un correo electrónico válido";
    }

    if (!values.referralCode) {
      newErrors.referralCode = "Selecciona un código de referido";
    }

    return newErrors;
  }, []);

  // Manejar cambios en los inputs
  const handleChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));

      // Limpiar error cuando el usuario empiece a escribir
      if (errors[name]) {
        setErrors((prev) => ({ ...prev, [name]: "" }));
      }
    },
    [errors]
  );

  // Manejar blur
  const handleBlur = useCallback(
    (e) => {
      const { name } = e.target;
      setTouched((prev) => ({ ...prev, [name]: true }));

      // Validar el campo específico
      const fieldErrors = validateForm(formData);
      if (fieldErrors[name]) {
        setErrors((prev) => ({ ...prev, [name]: fieldErrors[name] }));
      }
    },
    [formData, validateForm]
  );

  // Enviar invitación
  const submitInvitation = useCallback(
    async (values) => {
      try {
        setIsSubmitting(true);

        const selectedCode = referralCodes.find(
          (code) => code.code === values.referralCode
        );

        await SendEmailToChangeReferral(values);

        // Mostrar SweetAlert2 para éxito
        await Swal.fire({
          title: "¡Invitación enviada!",
          text: `Se ha enviado la invitación correctamente a ${values.email} con el código ${selectedCode?.code}`, // CORREGIDO: usar selectedCode?.code
          icon: "success",
          confirmButtonText: "Entendido",
          confirmButtonColor: "#009ee3",
          showClass: {
            popup: "animate__animated animate__fadeInDown",
          },
          hideClass: {
            popup: "animate__animated animate__fadeOutUp",
          },
        });

        // Resetear formulario
        setFormData({ email: "", referralCode: "" });
        setErrors({});
        setTouched({});

        return { success: true };
      } catch (error) {
        console.error("Error al enviar la invitación:", error);

        let errorTitle = "Error";
        let errorMessage = "Error al enviar la invitación";

        if (error && error.errors) {
          const errorKeys = Object.keys(error.errors);
          if (errorKeys.length > 0) {
            errorMessage = error.errors[errorKeys[0]][0];
          }
        } else if (error && error.httpStatus >= 500) {
          errorTitle = "No pudimos enviar la invitación";
          errorMessage =
            "Tuvimos un inconveniente al procesar tu solicitud. Inténtalo de nuevo en unos minutos. Si el problema continúa, contacta a soporte.";
        }

        Swal.fire({
          title: errorTitle,
          text: errorMessage,
          icon: "error",
          confirmButtonText: "Entendido",
          confirmButtonColor: "#ef4444",
        });

        throw error;
      } finally {
        setIsSubmitting(false);
      }
    },
    [referralCodes]
  );

  // Manejar envío del formulario
  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      const formErrors = validateForm(formData);
      setErrors(formErrors);
      setTouched({ email: true, referralCode: true });

      if (Object.keys(formErrors).length === 0) {
        await submitInvitation(formData);
      }
    },
    [formData, validateForm, submitInvitation]
  );

  return (
    <div className="invite-referrals">
      <div className="invite-referrals__header">
        <div className="invite-referrals__icon">
          <FaUserPlus style={{ fontSize: "30px" }} />
        </div>
        <div className="invite-referrals__title">
          <h1>Invitar Referidos a mi comunidad</h1>
        </div>
      </div>

      <div className="invite-referrals__content">
        <div className="invite-referrals__form-container">
          <div className="invite-referrals__section">
            <h3>
              <FaEnvelope className="section-icon" />
              Enviar Invitación
            </h3>
            <form className="invite-referrals__form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="email">
                  Correo electrónico del referido
                  <span className="required">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="ejemplo@correo.com"
                  className={touched.email && errors.email ? "input-error" : ""}
                />
                {touched.email && errors.email && (
                  <div className="error-message">{errors.email}</div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="referralCode">
                  Código de referido
                  <span className="required">*</span>
                </label>
                <select
                  id="referralCode"
                  name="referralCode"
                  value={formData.referralCode}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={
                    touched.referralCode && errors.referralCode
                      ? "input-error"
                      : ""
                  }
                  disabled={loadingCodes}
                >
                  <option value="">
                    {loadingCodes
                      ? "Cargando códigos..."
                      : "Selecciona un código de referido"}
                  </option>
                  {referralCodes.map((code, index) => (
                    <option
                      key={code.id || code.code || index}
                      value={code.code}
                    >
                      {code.code}
                    </option>
                  ))}
                </select>
                {touched.referralCode && errors.referralCode && (
                  <div className="error-message">{errors.referralCode}</div>
                )}
              </div>

              <div className="form-actions">
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={isSubmitting || loadingCodes}
                >
                  {isSubmitting ? (
                    <>
                      <span className="loading-spinner"></span>
                      Enviando invitación...
                    </>
                  ) : (
                    <>
                      <FaUserPlus />
                      Enviar invitación
                    </>
                  )}
                </button>
              </div>
            </form>{" "}
          </div>

          <div className="invite-referrals__info">
            <div className="info-card">
              <div className="info-card__content">
                <h4>¿Cómo funciona?</h4>
                <ul>
                  <li>Ingresa el correo del referido a invitar</li>
                  <li>Selecciona el código de tu comunidad</li>
                  <li>El referido recibirá un correo con la invitación</li>
                  <li>Debe aceptar la invitación para unirse a tu comunidad</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InviteReferrals;
